// components/NextIdeasList.tsx (Client Component)
'use client';

import React, { ChangeEvent, useState } from 'react';
import dynamic from 'next/dynamic';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { updateNextIdeas } from '@/server/articles/update-next-ideas';
import { useUiContent } from '@/components/ui-content-provider';
import { NextIdeas } from 'shared/src/types/article.type';
import { LocaleDate } from '@/components/locale-date';
import AutoResizeTextarea from './auto-resize-textarea';

// Dynamically import the client component
const CreateArticleButton = dynamic(() => import('./create-article-button'), { ssr: false });

interface NextIdeasListProps {
  nextIdeas: any[];
  nextIdeasTitle?: string;
  locale: LocaleDetails;
}

const NextIdeasList: React.FC<NextIdeasListProps> = ({ nextIdeas, nextIdeasTitle, locale }) => {
  const ui = useUiContent();
  // Check if DEV_MODE is true on the server
  const isDevMode = process.env.DEV_MODE === 'true';

  // Initialize state with the received ideas
  const [ideas, setIdeas] = useState<any[]>(nextIdeas);
  const [isModified, setIsModified] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveStatus, setSaveStatus] = useState<'success' | 'error' | null>(null);

  // Handler to update idea text
  const handleChange = (index: number, value: string) => {
    const updatedIdeas = [...ideas] as NextIdeas[];
    updatedIdeas[index] = {
      ...updatedIdeas[index],
      title: value,
    };

    setIdeas(updatedIdeas);
    setIsModified(nextIdeas[index].title !== value);
  };

  // Handler to save the updated ideas to the database
  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus(null);
    const response = await updateNextIdeas({ newNextIdeas: ideas });
    if (response !== 'ERROR') {
      setIsModified(false);
      setSaveStatus('success');
    } else {
      setSaveStatus('error');
    }
    setIsSaving(false);
  };

  return (
    <div className="relative mx-auto max-w-2xl p-4">
      {/* Subtle notification to inform users that fields are editable */}
      {nextIdeasTitle && <p className="whitespace-pre pb-8 font-bold">{nextIdeasTitle}</p>}

      <ul className="flex flex-col gap-2 pb-16 max-sm:pb-10">
        {ideas.map((idea, index) => (
          <li key={index} className={index !== 0 ? 'border-t pt-2' : ''}>
            {isDevMode && <CreateArticleButton idea={idea.title} lang={locale.path} />}
            {idea.new ? (
              <div className="flex justify-between">
                <span className="7 rounded-b-sm pb-2 text-xs font-bold">{ui.newProduct}</span>
                <LocaleDate date={idea.date ?? new Date().toISOString()} lang={locale} />
              </div>
            ) : (
              <div className="flex justify-end">
                <LocaleDate date={idea.date ?? new Date().toISOString()} lang={locale} />
              </div>
            )}

            {/* Editable textarea without styles */}
            <AutoResizeTextarea
              value={idea.title}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleChange(index, e.target.value)}
              aria-label={`Idea ${index + 1}`}
              className="w-full border-none bg-transparent pt-2 focus:outline-none"
              title="Click to edit"
              rows={1}
            />
          </li>
        ))}
      </ul>

      {/* Save Button */}
      {isModified && (
        <button onClick={handleSave} disabled={isSaving} className="absolute bottom-4 left-4 rounded px-4 py-2">
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      )}

      {/* Save Status Feedback */}
      {saveStatus === 'success' && <p className="absolute bottom-4 left-24 text-green-500">Saved successfully!</p>}
      {saveStatus === 'error' && <p className="absolute bottom-4 left-24 text-red-500">Failed to save. Please try again.</p>}
    </div>
  );
};

export default NextIdeasList;
